// Sophia — Multiple kid profiles.
// Each child has their own PIN, colour, avatar, progress, and favourites.
// Everything lives on this device only. PINs are salted SHA-256 digests.

import { getOption, setOption } from "./persistOptions";

export type KidProfile = {
  id: string;
  name: string;
  emoji: string;
  colour: string;
  age: number;
  pinHash: string;
  salt: string;
  hint: string;
  favLessons: string[];
  favCartoons: string[]; // KidItem.id
  xp: number;
  streak: number;
  lastActive: number;
  createdAt: number;
};

const LIST_KEY = "kidprofiles:list";
const ACTIVE_KEY = "kidprofiles:active";

async function digest(secret: string, salt: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`${salt}|kid|${secret}`));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function allProfiles(): KidProfile[] {
  return getOption<KidProfile[]>(LIST_KEY, []).sort((a, b) => a.createdAt - b.createdAt);
}

export function getProfile(id: string): KidProfile | undefined {
  return allProfiles().find((p) => p.id === id);
}

export function activeProfileId(): string {
  return getOption<string>(ACTIVE_KEY, "");
}

export function activeProfile(): KidProfile | undefined {
  const id = activeProfileId();
  return id ? getProfile(id) : undefined;
}

export function setActiveProfile(id: string): void {
  setOption(ACTIVE_KEY, id);
}

export function clearActiveProfile(): void {
  setOption(ACTIVE_KEY, "");
}

export async function createProfile(input: {
  name: string; emoji: string; colour: string; age: number; pin: string; hint?: string;
}): Promise<KidProfile> {
  const salt = Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map((b) => b.toString(16)).join("");
  const profile: KidProfile = {
    id: crypto.randomUUID(),
    name: input.name.slice(0, 24) || "Friend",
    emoji: input.emoji || "🐣",
    colour: input.colour || "#f472b6",
    age: Math.max(2, Math.min(18, Math.round(input.age))),
    pinHash: await digest(input.pin, salt),
    salt,
    hint: (input.hint || "").slice(0, 60),
    favLessons: [], favCartoons: [],
    xp: 0, streak: 0, lastActive: Date.now(), createdAt: Date.now(),
  };
  setOption(LIST_KEY, [...allProfiles(), profile]);
  return profile;
}

export function updateProfile(id: string, patch: Partial<KidProfile>): KidProfile | undefined {
  const list = allProfiles();
  const i = list.findIndex((p) => p.id === id);
  if (i < 0) return undefined;
  const next = { ...list[i], ...patch, id: list[i].id };
  list[i] = next;
  setOption(LIST_KEY, list);
  return next;
}

export function deleteProfile(id: string): void {
  setOption(LIST_KEY, allProfiles().filter((p) => p.id !== id));
  if (activeProfileId() === id) clearActiveProfile();
}

export async function verifyProfilePin(id: string, pin: string): Promise<boolean> {
  const p = getProfile(id);
  if (!p) return false;
  const ok = (await digest(pin, p.salt)) === p.pinHash;
  if (ok) updateProfile(id, { lastActive: Date.now() });
  return ok;
}

export async function resetProfilePin(id: string, newPin: string): Promise<void> {
  const p = getProfile(id);
  if (!p) return;
  updateProfile(id, { pinHash: await digest(newPin, p.salt) });
}

// ------------------------------------------------------------ favourites

export function toggleFavLesson(profileId: string, lessonId: string): void {
  const p = getProfile(profileId); if (!p) return;
  const set = new Set(p.favLessons);
  set.has(lessonId) ? set.delete(lessonId) : set.add(lessonId);
  updateProfile(profileId, { favLessons: [...set] });
}

export function toggleFavCartoon(profileId: string, cartoonId: string): void {
  const p = getProfile(profileId); if (!p) return;
  const set = new Set(p.favCartoons);
  set.has(cartoonId) ? set.delete(cartoonId) : set.add(cartoonId);
  updateProfile(profileId, { favCartoons: [...set] });
}

export function isFavLesson(profileId: string, lessonId: string): boolean {
  return getProfile(profileId)?.favLessons.includes(lessonId) ?? false;
}
export function isFavCartoon(profileId: string, cartoonId: string): boolean {
  return getProfile(profileId)?.favCartoons.includes(cartoonId) ?? false;
}

// ---------------------------------------------------------------- progress

export function addXp(profileId: string, points: number): void {
  const p = getProfile(profileId); if (!p) return;
  const today = new Date().toDateString();
  const last = new Date(p.lastActive).toDateString();
  const yesterday = new Date(Date.now() - 86_400_000).toDateString();
  const streak = last === today ? p.streak : last === yesterday ? p.streak + 1 : 1;
  updateProfile(profileId, {
    xp: p.xp + Math.max(0, points),
    streak,
    lastActive: Date.now(),
  });
}
